import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { BrokerClientApi, DrawApi } from '@apply/api';
import { DrawType } from '@apply/constants';
import { EsignModal } from '../../../esign';
import { useState } from 'react';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ManualAgreementStringBody, AgreementStringHeader } from '../jsxConstants';
import { GenericAlertPopup } from '@apply/components';
import { useAuthentication } from '../../../../../../../apps/bfi-apply/src/app/Login/hooks/useAuthentication';

interface ISubmitButtonProps {
    draw: DrawApi.IDraw;
    currentApproval?: DrawApi.IDrawBudgetApproval;
    isEquityMet: boolean;
    applicationInsights: ApplicationInsights;
    borrowerName: string;
    latestDrawBorrowerRequestTotal: number;
    onDrawBorrowerSubmitted: (id: number) => void;
    showFeedback: (value: boolean) => void;
    isCurrentUserDrawApprover: boolean;
    isReadOnly?: boolean;
}

export const SubmitButton = ({
    draw,
    currentApproval,
    isEquityMet,
    applicationInsights,
    borrowerName,
    latestDrawBorrowerRequestTotal,
    onDrawBorrowerSubmitted,
    showFeedback,
    isCurrentUserDrawApprover,
    isReadOnly = false,
}: ISubmitButtonProps): JSX.Element => {
    const { authentication } = useAuthentication();
    const isGeneralContractor = authentication?.user.isGeneralContractor ?? false;

    const drawApi = new DrawApi.DrawApi(applicationInsights);
    const brokerClientApi = new BrokerClientApi.BrokerClientApi(applicationInsights);

    const [isEsignOpen, set_isEsignOpen] = useState<boolean>(false);
    const [ipAddress, set_ipAddress] = useState<string>();

    // Check if this is an approval scenario based on draw state and current user
    // (a) GC has signed, (b) GC signature requires approval, (c) Principal hasn't signed yet,
    // (d) Current user is the approving user
    const isApprovalScenario =
        draw?.generalContractorSignatureId != null &&
        draw?.generalContractorSignatureRequiresApproval === true &&
        draw?.borrowerSignatureId == null &&
        isCurrentUserDrawApprover;

    const onSubmitClick = () => {
        // For approval scenarios, navigate to the sign page
        if (isApprovalScenario) {
            const drawBudgetId = draw.drawBudgetId;
            window.location.href = `/mobiledrawsign/${drawBudgetId}/${draw.id}?approve=true`;
            return;
        }

        // For regular submission, show the e-sign modal
        brokerClientApi.getIpAddress().then((response) => {
            if (response.statusCode !== 200) return;

            set_ipAddress(response.result);
            set_isEsignOpen(true);
        });
    };

    const onConfirmAgreement = async (font) => {
        const response = await drawApi.drawBorrowerSignature({
            drawId: draw.id,
            fontId: font.id,
            ipAddress: ipAddress,
        });

        set_isEsignOpen(false);

        if (response.statusCode !== 200) {
            GenericAlertPopup({
                title: 'Error',
                children: 'An unexpected error occurred while submitting the agreement. Please try again.',
                width: '350px',
            });
            return;
        }
        onDrawBorrowerSubmitted(draw.id);

        if (draw.drawType === 2) {
            showFeedback(true);
        }
    };

    const onRejectAgreement = () => {
        set_isEsignOpen(false);
    };

    if (
        (draw.borrowerSubmitted && !draw.needsBorrowerSignature) ||
        draw.drawType !== DrawType.Manual ||
        draw.isFinalized ||
        (isGeneralContractor && draw.hasGeneralContractorSignature)
    )
        return <></>;

    if (isReadOnly) return <DisabledButton titleText='This budget is read only.' />;

    if (!currentApproval) return <DisabledButton titleText='The budget is currently waiting for approval.' />;

    if (!isEquityMet) return <DisabledButton titleText='The equity requirement has not been met.' />;

    if (latestDrawBorrowerRequestTotal === 0) return <DisabledButton titleText='Please enter your budget request.' />;

    return (
        <>
            <button
                onClick={onSubmitClick}
                title={isApprovalScenario ? 'Approve Draw request.' : 'Submit Draw for review.'}
                className='button primary'
                style={{ zIndex: 1000, ...(isApprovalScenario && { fontSize: 'inherit' }) }}
            >
                {isApprovalScenario ? 'Approve' : 'Submit'}
            </button>
            <EsignModal
                agreementHtml={manualAgreementString}
                isOpen={isEsignOpen}
                signatureText={borrowerName}
                cancelCallback={onRejectAgreement}
                confirmCallback={(font) => onConfirmAgreement(font)}
                ipAddress={ipAddress}
                rejectLabel='I DO NOT ACCEPT THIS AGREEMENT'
                acceptLabel='I ACCEPT THIS AGREEMENT'
            />
        </>
    );
};

const DisabledButton = ({ titleText }: { titleText: string }) => {
    return (
        <button
            disabled={true}
            title={titleText}
            className='button primary'
            style={{ zIndex: 2001 }}
        >
            Submit
        </button>
    );
};

const manualAgreementString = ReactDOMServer.renderToStaticMarkup(
    <div style={{ paddingLeft: 20, fontSize: 20 }}>
        <p style={{ fontSize: 24, fontWeight: 600 }}>
            <AgreementStringHeader />
        </p>
        <ManualAgreementStringBody />
    </div>
);
